<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES, VOICE_CALL_STATUS } from '../constants';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import WhatsAppInConversationCallBar from 'dashboard/components/WhatsAppInConversationCallBar.vue';

const LABEL_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS',
  [VOICE_CALL_STATUS.COMPLETED]: 'CONVERSATION.VOICE_CALL.CALL_ENDED',
  [VOICE_CALL_STATUS.REJECTED]: 'CONVERSATION.VOICE_CALL.CALL_REJECTED',
};

const SUBTEXT_MAP = {
  [VOICE_CALL_STATUS.RINGING]: 'CONVERSATION.VOICE_CALL.NOT_ANSWERED_YET',
  [VOICE_CALL_STATUS.COMPLETED]: 'CONVERSATION.VOICE_CALL.CALL_ENDED',
  [VOICE_CALL_STATUS.REJECTED]: 'CONVERSATION.VOICE_CALL.CALL_REJECTED',
};

const ICON_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'i-ph-phone-call',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'i-ph-phone-x',
  [VOICE_CALL_STATUS.REJECTED]: 'i-ph-phone-x',
  [VOICE_CALL_STATUS.FAILED]: 'i-ph-phone-x',
};

const BG_COLOR_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'bg-n-teal-9',
  [VOICE_CALL_STATUS.RINGING]: 'bg-n-teal-9 animate-pulse',
  [VOICE_CALL_STATUS.COMPLETED]: 'bg-n-slate-11',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'bg-n-ruby-9',
  [VOICE_CALL_STATUS.REJECTED]: 'bg-n-ruby-9',
  [VOICE_CALL_STATUS.FAILED]: 'bg-n-ruby-9',
};

const { contentAttributes, messageType, id, conversationId, content } = useMessageContext();

const data = computed(() => contentAttributes.value?.data);
const status = computed(() => data.value?.status?.toString());
const duration = computed(() => data.value?.duration);

const isOutbound = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);
const isFailed = computed(() =>
  [VOICE_CALL_STATUS.NO_ANSWER, VOICE_CALL_STATUS.REJECTED, VOICE_CALL_STATUS.FAILED].includes(status.value)
);

// Helper to format duration
const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const labelKey = computed(() => {
  if (LABEL_MAP[status.value]) return LABEL_MAP[status.value];
  if (status.value === VOICE_CALL_STATUS.RINGING) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.OUTGOING_CALL'
      : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
  }
  return isFailed.value
    ? 'CONVERSATION.VOICE_CALL.MISSED_CALL'
    : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
});

// Label text - use content from backend if available (it includes duration)
const labelText = computed(() => {
  // For completed calls with duration, use the message content directly
  if (status.value === VOICE_CALL_STATUS.COMPLETED && content.value) {
    return content.value;
  }
  // For rejected calls, use the message content directly
  if (status.value === VOICE_CALL_STATUS.REJECTED && content.value) {
    return content.value;
  }
  // Otherwise use i18n
  return labelKey.value;
});

const subtextKey = computed(() => {
  if (SUBTEXT_MAP[status.value]) return SUBTEXT_MAP[status.value];
  if (status.value === VOICE_CALL_STATUS.IN_PROGRESS) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.THEY_ANSWERED'
      : 'CONVERSATION.VOICE_CALL.YOU_ANSWERED';
  }
  return isFailed.value
    ? 'CONVERSATION.VOICE_CALL.NO_ANSWER'
    : 'CONVERSATION.VOICE_CALL.NOT_ANSWERED_YET';
});

// Subtext - use content or formatted duration for completed calls
const subtextText = computed(() => {
  // For completed calls, show duration if available
  if (status.value === VOICE_CALL_STATUS.COMPLETED && duration.value) {
    return formatDuration(duration.value);
  }
  // For rejected calls, no subtext needed (already in label)
  if (status.value === VOICE_CALL_STATUS.REJECTED) {
    return '';
  }
  // Otherwise use i18n
  return subtextKey.value;
});

const iconName = computed(() => {
  if (ICON_MAP[status.value]) return ICON_MAP[status.value];
  return isOutbound.value ? 'i-ph-phone-outgoing' : 'i-ph-phone-incoming';
});

const bgColor = computed(() => BG_COLOR_MAP[status.value] || 'bg-n-teal-9');

// Check if we should use i18n or raw text
const useI18nForLabel = computed(() => {
  return !(status.value === VOICE_CALL_STATUS.COMPLETED && content.value) &&
         !(status.value === VOICE_CALL_STATUS.REJECTED && content.value);
});

const useI18nForSubtext = computed(() => {
  return !(status.value === VOICE_CALL_STATUS.COMPLETED && duration.value) &&
         status.value !== VOICE_CALL_STATUS.REJECTED;
});
</script>

<template>
  <BaseBubble class="p-0 border-none" hide-meta>
    <div class="flex overflow-hidden flex-col w-full max-w-xs">
      <div class="flex gap-3 items-center p-3 w-full">
        <div
          class="flex justify-center items-center rounded-full size-10 shrink-0"
          :class="bgColor"
        >
          <Icon
            class="size-5"
            :icon="iconName"
            :class="{
              'text-n-slate-1': status === VOICE_CALL_STATUS.COMPLETED,
              'text-white': status !== VOICE_CALL_STATUS.COMPLETED,
            }"
          />
        </div>

        <div class="flex overflow-hidden flex-col flex-grow">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ useI18nForLabel ? $t(labelText) : labelText }}
          </span>
          <span v-if="subtextText" class="text-xs text-n-slate-11">
            {{ useI18nForSubtext ? $t(subtextText) : subtextText }}
          </span>
        </div>
      </div>
    </div>

    <!-- In-conversation audio bar when call is active -->
    <WhatsAppInConversationCallBar
      v-if="data && status === VOICE_CALL_STATUS.RINGING"
      :message-id="id"
      :conversation-id="conversationId"
      :call-data="data"
    />
  </BaseBubble>
</template>
